import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TerritoryMapSection = ({ territoryData, suppliers, countries, onRefresh }) => {
  const [selectedTerritory, setSelectedTerritory] = useState(null);

  const getTerritoryColor = (supplierCount) => {
    if (supplierCount === 0) return 'bg-gray-200';
    if (supplierCount <= 2) return 'bg-yellow-200';
    if (supplierCount <= 5) return 'bg-green-200';
    return 'bg-blue-200';
  };

  const getTerritoryBorderColor = (supplierCount) => {
    if (supplierCount === 0) return 'border-gray-400';
    if (supplierCount <= 2) return 'border-yellow-400';
    if (supplierCount <= 5) return 'border-green-400';
    return 'border-blue-400';
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Territory Map Grid */}
      <div className="lg:col-span-2 bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Carte des Territoires</h2>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 border border-gray-400 rounded"></div>
              <span className="text-gray-600">Aucun</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-200 border border-yellow-400 rounded"></div>
              <span className="text-gray-600">1-2</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-200 border border-green-400 rounded"></div>
              <span className="text-gray-600">3-5</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-200 border border-blue-400 rounded"></div>
              <span className="text-gray-600">5+</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {territoryData?.map(territory => (
            <button
              key={territory?.countryId}
              onClick={() => setSelectedTerritory(territory)}
              className={`p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                getTerritoryColor(territory?.suppliers?.length)
              } ${
                getTerritoryBorderColor(territory?.suppliers?.length)
              } ${
                selectedTerritory?.countryId === territory?.countryId ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <div className="text-left">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold">{territory?.countryCode}</span>
                  <Icon name="MapPin" className="w-5 h-5 text-gray-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{territory?.countryName}</h3>
                <div className="space-y-1 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Icon name="Users" className="w-4 h-4" />
                    <span>{territory?.suppliers?.length} fournisseurs</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Settings" className="w-4 h-4" />
                    <span>{territory?.totalMachines} machines</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {territoryData?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Map" className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucune donnée territoriale disponible</p>
          </div>
        )}
      </div>
      {/* Territory Details Panel */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        {selectedTerritory ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Détails du Territoire</h3>
              <button
                onClick={() => setSelectedTerritory(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="X" className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="MapPin" className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-gray-900">{selectedTerritory?.countryName}</span>
                </div>
                <p className="text-sm text-gray-600">Code: {selectedTerritory?.countryCode}</p>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Statistiques</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Fournisseurs actifs</span>
                    <span className="font-semibold text-gray-900">{selectedTerritory?.suppliers?.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Total machines</span>
                    <span className="font-semibold text-gray-900">{selectedTerritory?.totalMachines}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Machines par fournisseur</span>
                    <span className="font-semibold text-gray-900">
                      {selectedTerritory?.suppliers?.length > 0
                        ? (selectedTerritory?.totalMachines / selectedTerritory?.suppliers?.length)?.toFixed(1)
                        : 0}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Fournisseurs</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedTerritory?.suppliers?.map(supplier => (
                    <div key={supplier?.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{supplier?.companyName || supplier?.fullName}</p>
                        <p className="text-xs text-gray-500">{supplier?.machineCount} machines</p>
                      </div>
                      <Icon name="ChevronRight" className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                  {selectedTerritory?.suppliers?.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">Aucun fournisseur assigné</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="MousePointer" className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Sélectionnez un territoire pour voir les détails</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TerritoryMapSection;